// What the campaign screens (UI-2) share: the steps, the list's tabs, a
// country's audience, local time per country, the totals over a group's
// countries, and the report figures a list row shows. No request is made here;
// the pages own those.

import type {
  CampaignGroupState,
  CampaignGroupSummary,
  CampaignState,
  CountryAudienceInput,
  CountryEstimate,
  CountryWording,
  GroupAudience,
  GroupCountry,
  Localization,
  NamedSegment,
  ReportRow,
  SendingIdentity,
  WordingStatus,
} from "@/lib/api";
import { count } from "@/components/emc/format";
import {
  atViewer,
  cityOf,
  countryClock,
  timeAtViewer,
  wallToMoment,
  withCountryClock,
} from "@/components/emc/time";

// --- the editor's steps ------------------------------------------------------

export const STEPS = [
  { key: "alapok", label: "Alapok" },
  { key: "cimzettek", label: "Címzettek" },
  { key: "tartalom", label: "Tartalom" },
  { key: "idozites", label: "Időzítés" },
  { key: "attekintes", label: "Áttekintés és küldés" },
] as const;

export type StepKey = (typeof STEPS)[number]["key"];

export function isStepKey(value: string | null): value is StepKey {
  return STEPS.some((step) => step.key === value);
}

export function nextStep(key: StepKey): StepKey | null {
  const index = STEPS.findIndex((step) => step.key === key);
  return STEPS[index + 1]?.key ?? null;
}

export function previousStep(key: StepKey): StepKey | null {
  const index = STEPS.findIndex((step) => step.key === key);
  return index > 0 ? (STEPS[index - 1]?.key ?? null) : null;
}

// --- the group's states, as the screens group them ---------------------------

/** The composition can still change (4.3: countries, letter and A/B are set in
 * `draft`; `review` is the same group, locked, and one step from sending). */
export const COMPOSING: ReadonlySet<CampaignGroupState> = new Set(["draft", "review"]);

/** Released or about to be: the numbers move, the page refreshes itself. */
export const MOVING: ReadonlySet<CampaignGroupState> = new Set([
  "scheduled",
  "ab_testing",
  "ab_deciding",
  "sending",
]);

/** The list's tabs. A partition of the group states: every group is in
 * exactly one tab besides "Összes". A group whose country failed is still
 * `sending` (4.3: the failure is the country's, and it can be restarted), so
 * it stays under "Fut" and its row says which country failed. */
export const TABS: { key: string; label: string; states: CampaignGroupState[] }[] = [
  { key: "osszes", label: "Összes", states: [] },
  { key: "vazlat", label: "Vázlat", states: ["draft", "review"] },
  { key: "utemezett", label: "Ütemezett", states: ["scheduled"] },
  { key: "fut", label: "Fut", states: ["ab_testing", "ab_deciding", "sending"] },
  { key: "elkuldve", label: "Elküldve", states: ["done"] },
  { key: "leallt", label: "Leállt / hibás", states: ["cancelled", "failed"] },
];

/** A country campaign that can still take pause, resume or cancel. */
export const COUNTRY_LIVE: ReadonlySet<CampaignState> = new Set([
  "draft",
  "scheduled",
  "snapshotting",
  "sending",
  "paused",
]);

// --- local time ----------------------------------------------------------------
//
// A group is scheduled at one wall clock ("2026-10-30T09:00"), which each
// country reads on its own clock (`sending_account.timezone`); the API turns it
// into UTC. A screen shows the reader's own clock first and the country's in
// brackets (owner, 2026-10-26): `startsLine`, `components/emc/time.ts`.

const WALL = new Map<string, Intl.DateTimeFormat>();

function wallFormat(timeZone: string): Intl.DateTimeFormat {
  let format = WALL.get(timeZone);
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    WALL.set(timeZone, format);
  }
  return format;
}

/** A moment as the wall clock in `timeZone` shows it: "2026-10-30T09:00". */
export function wallClock(moment: Date, timeZone: string): string {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = wallFormat(timeZone).formatToParts(moment);
  } catch {
    parts = wallFormat("UTC").formatToParts(moment);
  }
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? "00";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
}

/** Now, on a country's wall clock, to the minute. */
export function nowIn(timeZone: string): string {
  return wallClock(new Date(), timeZone);
}

const LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

export function isLocal(value: string): boolean {
  return LOCAL.test(value);
}

/** "2026-10-30T09:00" as a reader writes it: "2026. 10. 30. 09:00". */
export function showLocal(local: string): string {
  const match = LOCAL.exec(local);
  if (match === null) return local || "—";
  return `${match[1]}. ${match[2]}. ${match[3]}. ${match[4]}:${match[5]}`;
}

export function hourOf(local: string): number | null {
  const match = LOCAL.exec(local);
  return match === null ? null : Number(match[4]);
}

export interface StartsLine {
  /** The earliest start, on the reader's clock: "2026. 10. 30. 08:00". */
  viewer: string;
  /** The countries' own clocks: "Bukarest 09:00", "országonként helyi 09:00"
   * when they all read the same, or "Bukarest 09:00 · Budapest 10:00". Null
   * when no country's zone is known. */
  local: string | null;
}

/** When a campaign's countries start, in one line. `at` is a moment (UTC from
 * the API, or a draft's wall clock already turned into one). */
export function startsLine(
  starts: { country: string; at: string | Date | null }[],
  zoneOf: (country: string) => string | null | undefined,
  viewer?: string,
): StartsLine | null {
  const timed = starts
    .map((row) => ({ ...row, moment: row.at === null ? null : new Date(row.at) }))
    .filter((row): row is typeof row & { moment: Date } => row.moment !== null && !Number.isNaN(row.moment.getTime()));
  if (timed.length === 0) return null;
  const first = timed.reduce((a, b) => (b.moment < a.moment ? b : a));
  const clocks = timed.flatMap((row) => {
    const zone = zoneOf(row.country);
    const clock = zone ? countryClock(row.moment, zone, viewer) : null;
    return clock === null || !zone ? [] : [{ clock, wall: clock.slice(cityOf(zone).length + 1) }];
  });
  let local: string | null = null;
  if (clocks.length === 1 && timed.length === 1) {
    local = clocks[0]!.clock;
  } else if (clocks.length > 0) {
    const walls = new Set(clocks.map((row) => row.wall));
    local =
      walls.size === 1 && clocks.length === timed.length
        ? `országonként helyi ${clocks[0]!.wall}`
        : [...new Set(clocks.map((row) => row.clock))].join(" · ");
  }
  return { viewer: atViewer(first.moment, viewer), local };
}

/** `startsLine` as one string: "2026. 10. 30. 08:00 (Bukarest 09:00)". */
export function startsText(line: StartsLine | null): string {
  if (line === null) return "—";
  return line.local === null ? line.viewer : `${line.viewer} (${line.local})`;
}

/** A draft's starts: each country's wall clock as the moment it is there. */
export function draftStarts(
  draft: TimingDraft,
  countries: string[],
  zoneOf: (country: string) => string,
): { country: string; at: Date | null }[] {
  return countries.map((country) => ({
    country,
    at: wallToMoment(localMomentFor(draft, country, zoneOf(country)), zoneOf(country)),
  }));
}

/** One country's start under a draft, for a table cell: "most", or the
 * reader's clock with the country's in brackets. */
export function draftStartText(draft: TimingDraft, country: string, zone: string): string {
  if (draft.mode === "now") return "most";
  return withCountryClock(wallToMoment(localMomentFor(draft, country, zone), zone), zone);
}

/** `[start, end)` in local hours; `end < start` is an overnight window. */
export function insideWindow(hour: number, start: number, end: number): boolean {
  return start <= end ? hour >= start && hour < end : hour >= start || hour < end;
}

/** A country's sending window on the reader's clock, the country's hours in
 * brackets: "07:00–19:00 (Bukarest 8–20 óra)". `day` ("2026-10-30") picks the
 * side of a daylight-saving switch; without it, today on the country's clock. */
export function windowText(identity: SendingIdentity, day?: string, viewer?: string): string {
  const local = `${identity.send_window_start_hour}–${identity.send_window_end_hour} óra`;
  const zone = identity.timezone;
  if (!zone) return local;
  const on = day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : nowIn(zone).slice(0, 10);
  const at = (hour: number) => wallToMoment(`${on}T${String(hour % 24).padStart(2, "0")}:00`, zone);
  const start = at(identity.send_window_start_hour);
  const end = at(identity.send_window_end_hour);
  if (start === null || end === null) return local;
  return `${timeAtViewer(start, viewer)}–${timeAtViewer(end, viewer)} (${cityOf(zone)} ${local})`;
}

// --- the timing the marketer chose, until it is sent ------------------------
//
// The API stores a schedule only when it is made (`review → scheduled`), so the
// choice lives in the browser until then — per campaign, and only as a
// convenience: a lost draft is a choice made again, never a wrong send.

export interface TimingDraft {
  mode: "now" | "later";
  /** "2026-10-30" */
  date: string;
  /** "09:00" */
  time: string;
  /** A different wall clock for some countries: {"RO": "2026-10-30T10:00"}. */
  overrides: Record<string, string>;
}

const TIMING_KEY = "emc.campaign.timing.";

export function emptyTiming(): TimingDraft {
  return { mode: "now", date: "", time: "09:00", overrides: {} };
}

export function loadTiming(groupId: number): TimingDraft {
  try {
    const raw = localStorage.getItem(`${TIMING_KEY}${groupId}`);
    if (raw === null) return emptyTiming();
    const parsed = JSON.parse(raw) as Partial<TimingDraft>;
    return {
      mode: parsed.mode === "later" ? "later" : "now",
      date: typeof parsed.date === "string" ? parsed.date : "",
      time: typeof parsed.time === "string" ? parsed.time : "09:00",
      overrides:
        parsed.overrides !== null && typeof parsed.overrides === "object"
          ? Object.fromEntries(
              Object.entries(parsed.overrides).filter(
                (entry): entry is [string, string] => typeof entry[1] === "string",
              ),
            )
          : {},
    };
  } catch {
    return emptyTiming();
  }
}

export function saveTiming(groupId: number, draft: TimingDraft): void {
  try {
    localStorage.setItem(`${TIMING_KEY}${groupId}`, JSON.stringify(draft));
  } catch {
    // Private windows and blocked storage: the choice lasts for the page.
  }
}

export function forgetTiming(groupId: number): void {
  try {
    localStorage.removeItem(`${TIMING_KEY}${groupId}`);
  } catch {
    // Nothing to forget where nothing could be stored.
  }
}

/** The wall clock a country will send at under this draft. "Most" is each
 * country's own now — the moment is the same everywhere, the clocks differ. */
export function localMomentFor(draft: TimingDraft, country: string, timeZone: string): string {
  if (draft.mode === "now") return nowIn(timeZone);
  return draft.overrides[country] || `${draft.date}T${draft.time}`;
}

/** What `POST /schedule` takes: one wall clock and the countries that differ.
 * For "Most" every country gets its own current wall clock, rounded down to
 * the minute, so each is released on the runner's next pass. */
export function scheduleOf(
  draft: TimingDraft,
  countries: string[],
  zoneOf: (country: string) => string,
): { atLocal: string; overrides: Record<string, string> } {
  if (draft.mode === "now") {
    const overrides = Object.fromEntries(
      countries.map((country) => [country, nowIn(zoneOf(country))]),
    );
    return { atLocal: overrides[countries[0] ?? ""] ?? nowIn("UTC"), overrides };
  }
  const atLocal = `${draft.date}T${draft.time}`;
  const overrides = Object.fromEntries(
    Object.entries(draft.overrides).filter(
      ([country, value]) => countries.includes(country) && isLocal(value) && value !== atLocal,
    ),
  );
  return { atLocal, overrides };
}

// --- one country's audience (UI-10, UI-11) -------------------------------------
//
// A country's subscription is its contact rows — one per address and country —
// so "the whole country" is the ordinary send: everyone in it who may be
// mailed. Segments are the narrower choice, ticked (owner, 2026-10-26): the
// ticked ones are the audience, their union, and an unticked one is simply not
// part of it. "Összes" is the whole country. Nothing ticked is nobody chosen,
// never everyone: the whole country is said on purpose, not reached by
// unticking the last box.

/** One country's audience while it is being chosen, before it is added. */
export interface AudienceDraft {
  /** "Összes": the whole country. Ticking a segment turns it off. */
  wholeCountry: boolean;
  /** The ticked segments, in the order ticked. */
  segmentIds: number[];
  /** Exclusions, added one at a time; a ticked segment is never one. */
  excludeIds: number[];
  /** Days; "" or "0" is "only the country's own cap". */
  capDays: string;
}

/** The pihentetés a new country starts with, and the ones offered as buttons. */
export const DEFAULT_CAP_DAYS = 7;
export const CAP_PRESETS = [0, 3, 7, 14] as const;

/** A new country's audience: the whole country, nothing excluded, 7 days' rest. */
export function emptyAudience(): AudienceDraft {
  return { wholeCountry: true, segmentIds: [], excludeIds: [], capDays: String(DEFAULT_CAP_DAYS) };
}

/** A segment's box ticked or unticked. Ticked, it is the audience's and no
 * longer an exclusion, and the whole country goes off. */
export function tickSegment(draft: AudienceDraft, id: number, on: boolean): AudienceDraft {
  if (!on) return { ...draft, segmentIds: draft.segmentIds.filter((row) => row !== id) };
  return {
    ...draft,
    wholeCountry: false,
    segmentIds: draft.segmentIds.includes(id) ? draft.segmentIds : [...draft.segmentIds, id],
    excludeIds: draft.excludeIds.filter((row) => row !== id),
  };
}

/** The "Összes" button: the whole country, no segment ticked. */
export function wholeCountry(draft: AudienceDraft): AudienceDraft {
  return { ...draft, wholeCountry: true, segmentIds: [] };
}

export function addExclusion(draft: AudienceDraft, id: number): AudienceDraft {
  if (draft.excludeIds.includes(id) || draft.segmentIds.includes(id)) return draft;
  return { ...draft, excludeIds: [...draft.excludeIds, id] };
}

export function removeExclusion(draft: AudienceDraft, id: number): AudienceDraft {
  return { ...draft, excludeIds: draft.excludeIds.filter((row) => row !== id) };
}

/** What "+ kizárás hozzáadása" offers: neither ticked nor already excluded. */
export function exclusionChoices<T extends { id: number }>(segments: T[], draft: AudienceDraft): T[] {
  return segments.filter((row) => !draft.segmentIds.includes(row.id) && !draft.excludeIds.includes(row.id));
}

/** The longest cap a campaign takes, in days: the API's bound, the same as
 * the account's own cap. */
export const CAP_DAYS_MAX = 365;

/** What stops this country from being added, in the step's words; null when
 * nothing does. */
export function audienceProblem(country: string, draft: AudienceDraft): string | null {
  if (!draft.wholeCountry && draft.segmentIds.length === 0) {
    return `${country}: jelölj be legalább egy szegmenst, vagy válaszd az Összes gombot.`;
  }
  const days = Number(draft.capDays || "0");
  if (!Number.isInteger(days) || days < 0) {
    return `${country}: a gyakorisági korlát egész számú nap (0 = csak az ország saját korlátja).`;
  }
  if (days > CAP_DAYS_MAX) {
    return `${country}: a gyakorisági korlát legfeljebb ${CAP_DAYS_MAX} nap.`;
  }
  return null;
}

/** What `POST /countries` (and the live count) takes for one country: the
 * whole country or the ticked segments, exactly one of the two. */
export function audienceInput(country: string, draft: AudienceDraft): CountryAudienceInput {
  const segmentIds = draft.wholeCountry ? [] : draft.segmentIds;
  return {
    country,
    whole_country: draft.wholeCountry,
    segment_ids: segmentIds,
    exclude_segment_ids: draft.excludeIds.filter((id) => !segmentIds.includes(id)),
    frequency_cap_days: Number(draft.capDays || "0"),
  };
}

function segmentName(row: NamedSegment): string {
  const size = row.member_count === null ? "" : ` (${count(row.member_count)} fő)`;
  return `${row.name ?? `#${row.id}`}${size}`;
}

/** An added country's audience in one line, by name. */
export function audienceText(audience: GroupAudience, capDays: number): string {
  const parts: string[] = [];
  if (audience.whole_country) parts.push("az egész ország");
  // An older API names only `segment`.
  const segments = audience.segments ?? (audience.segment ? [audience.segment] : []);
  if (segments.length > 0) {
    parts.push(`${segments.length === 1 ? "szegmens" : "szegmensek"}: ${segments.map(segmentName).join(", ")}`);
  }
  if (audience.exclude_segments.length > 0) {
    parts.push(
      `kivéve: ${audience.exclude_segments.map((row) => row.name ?? `#${row.id}`).join(", ")}`,
    );
  }
  if (capDays > 0) parts.push(`aki ${capDays} napon belül kapott levelet, kimarad`);
  return parts.join(" · ") || "—";
}

// --- what a country's audience leaves out, and its day (UI-11) -------------------
//
// Owner, 2026-10-26: "rendesen lássam a számokat, ne vezessen félre". The
// buckets after `not_in_audience` are the audience's own (the API asks
// "in the audience?" first), so "kimarad" is people this campaign was about.

interface LeftOutCounts {
  suppressed: number;
  frequency_capped: number;
  excluded_by_segment: number;
  absent?: number;
  handover?: number;
  group_duplicate?: number;
}

const LEFT_OUT: [keyof LeftOutCounts, string][] = [
  ["frequency_capped", "pihentetés"],
  ["group_duplicate", "másik országból kapja"],
  ["suppressed", "kizárt"],
  ["excluded_by_segment", "kizáró szegmens"],
  ["absent", "nincs jelen"],
  ["handover", "a Klaviyóé"],
];

/** Inside the audience, not getting it: the number and its reasons. */
export function leftOut(row: LeftOutCounts): { total: number; text: string } {
  const parts = LEFT_OUT.map(([key, label]) => [row[key] ?? 0, label] as const).filter(([n]) => n > 0);
  const total = parts.reduce((sum, [n]) => sum + n, 0);
  return {
    total,
    text: total === 0 ? "nem marad ki senki" : `kimarad: ${parts.map(([n, label]) => `${count(n)} ${label}`).join(" · ")}`,
  };
}

export interface DayFigures {
  country: string;
  account: string;
  recipients: number;
  /** Goes out today: the country's part of its SES account's day, at most
   * the recipients. */
  today: number;
  /** The rest, on the days after. */
  after: number;
  leftOut: number;
  leftOutText: string;
}

/** One country's row on the review step. Without a split (an account warmed
 * per pool) the pool's own room decides. */
export function dayFigures(row: CountryEstimate): DayFigures {
  const room = row.country_room_today ?? row.pool_room_today;
  const today = Math.max(0, Math.min(room, row.recipients));
  const out = leftOut(row);
  return {
    country: row.country,
    account: row.ses_account || row.pool_code,
    recipients: row.recipients,
    today,
    after: row.recipients - today,
    leftOut: out.total,
    leftOutText: out.text,
  };
}

export interface AccountDay {
  account: string;
  today: number;
  countries: { country: string; today: number }[];
}

/** Today per SES account: its countries' figures, which add up to its line. */
export function accountDays(rows: CountryEstimate[]): AccountDay[] {
  const byAccount = new Map<string, AccountDay>();
  for (const row of rows) {
    const figures = dayFigures(row);
    const day = byAccount.get(figures.account) ?? { account: figures.account, today: 0, countries: [] };
    day.today += figures.today;
    day.countries.push({ country: figures.country, today: figures.today });
    byAccount.set(figures.account, day);
  }
  return [...byAccount.values()]
    .map((day) => ({ ...day, countries: [...day.countries].sort((a, b) => a.country.localeCompare(b.country)) }))
    .sort((a, b) => a.account.localeCompare(b.account));
}

/** "A fiók ma: 10 000 · RO 10 000". */
export function accountDayText(day: AccountDay): string {
  return [`${day.account} fiók ma: ${count(day.today)}`, ...day.countries.map((row) => `${row.country} ${count(row.today)}`)].join(
    " · ",
  );
}

// --- a group's numbers, summed over its countries -----------------------------

export interface Totals {
  recipients: number;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  failed: number;
  unknown: number;
  skipped: number;
}

export function totalsOf(countries: GroupCountry[]): Totals {
  const sum = (pick: (row: GroupCountry) => number) =>
    countries.reduce((total, row) => total + pick(row), 0);
  return {
    recipients: sum((row) => row.recipient_total),
    sent: sum((row) => row.sent_count),
    delivered: sum((row) => row.delivered_count),
    bounced: sum((row) => row.bounced_count),
    complained: sum((row) => row.complained_count),
    failed: sum((row) => row.failed_count),
    unknown: sum((row) => row.unknown_count),
    skipped: sum((row) => row.skipped_count),
  };
}

/** Recipients are counted when a country is assembled; before that there is
 * no number to show, which is not the same as zero. */
export function assembled(countries: GroupCountry[]): boolean {
  return countries.some((row) => row.recipient_total > 0 || row.started_at !== null);
}

// --- what the report rows say about a group --------------------------------------
//
// The reports divide by `sent` (emc_db.reports.figures), and so does this. A
// figure the warehouse did not give for a country that sent something makes
// the group's figure unknown: a sum over the countries that answered would be
// a smaller number presented as the whole.

export interface Engagement {
  sent: number;
  delivered: number;
  /** Every open, Apple's automatic ones included; and of those the automatic. */
  opens: number | null;
  opensMachine: number | null;
  clicks: number | null;
  unsubscribed: number | null;
}

export function engagementOf(
  countries: GroupCountry[],
  reports: ReadonlyMap<number, ReportRow>,
): Engagement | null {
  const rows = countries
    .map((row) => reports.get(row.campaign_id))
    .filter((row): row is ReportRow => row !== undefined);
  if (rows.length === 0) return null;
  const sent = rows.reduce((total, row) => total + (row.sent ?? 0), 0);
  const delivered = rows.reduce((total, row) => total + (row.delivered ?? 0), 0);
  const add = (pick: (row: ReportRow) => number | null): number | null => {
    let total = 0;
    for (const row of rows) {
      const value = pick(row);
      if (value === null) {
        if ((row.sent ?? 0) > 0) return null;
        continue;
      }
      total += value;
    }
    return total;
  };
  return {
    sent,
    delivered,
    opens: add((row) => row.opens_total),
    opensMachine: add((row) => row.opens_machine),
    clicks: add((row) => row.unique_clicks),
    unsubscribed: add((row) => row.unsubscribed),
  };
}

// --- localised wording (4.3) -------------------------------------------------------

/** A country's own wording in the words the matrix uses for it and for every
 * A/B cell (`localize.matrix`): missing, machine, human, approved — or stale,
 * an approval of a source that has changed since. One vocabulary, so the
 * country card and the matrix beside it never name one state two ways. */
export function wordingStatusOf(localization: Localization | null, stale: boolean): WordingStatus {
  if (localization === null) return "missing";
  if (localization.approved_at !== null) return stale ? "stale" : "approved";
  return localization.translated_by === "machine" ? "machine" : "human";
}

/** The subject lines one country's recipients get, for the review preview.
 *
 * Without an A/B test it is the letter's own. With one, nobody gets that: the
 * test share goes out under the arms' subjects and the remainder under the
 * winner's — so each arm's, the one the schedule freezes onto the country's arm
 * (in a localised group the country's own cell, `row`). An empty one is the
 * letter's own, as the sender lays an arm over the letter. */
export function subjectsSent(
  summary: Pick<CampaignGroupSummary, "mode" | "ab" | "ab_arms">,
  row: Pick<CountryWording, "arms"> | undefined,
  letterSubject: string,
): { key: string | null; subject: string }[] {
  if (summary.ab === null || summary.ab_arms.length === 0) {
    return [{ key: null, subject: letterSubject }];
  }
  return summary.ab_arms.map((arm) => {
    const typed =
      summary.mode === "localized"
        ? (row?.arms.find((cell) => cell.key === arm.key)?.subject ?? "")
        : arm.subject;
    return { key: arm.key, subject: typed.trim() || letterSubject };
  });
}

// --- words -----------------------------------------------------------------------

/** "3 országba", "1 országba" — the illative the send button needs. */
export function intoCountries(count: number): string {
  return `${count.toLocaleString("hu-HU")} országba`;
}

export function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}
